<template>
  <div class="ret-cards-wrapper">
    <!-- Item editor -->
    <v-card v-if="! dataLoaded" class="ret-card">
      <v-progress-circular indeterminate color="primary" class="ret-center-absolute"></v-progress-circular>
    </v-card>
    <v-card v-if="dataLoaded" class="ret-card">
      <v-card-title>
        <h3 class="headline">
          <span v-if="editedItem.code">[{{ editedItem.code }}]</span>
          {{ editedItem.name }}
        </h3>
        <v-spacer />
        <v-select
          v-model="language"
          item-text="name"
          item-value="code"
          :items="availableLanguages"
          :label="$t('translation')"
          class="flex xs6 sm4 md3 lg2"
        ></v-select>
        <v-menu v-if="editedItem.id" open-on-hover offset-y>
          <template v-slot:activator="{ on }">
            <v-btn text icon class="caption" v-on="on" data-test="more">
              <v-icon>more_vert</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="openCloneDialog" data-test="clone">{{ $t('checklists.clone') }}</v-list-item>
            <!-- <v-list-item @click="onDestroy(editedItem)" color="warn">{{ $t('Archive') }}</v-list-item> -->
          </v-list>
        </v-menu>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-row>
            <v-col xs="12" sm="2">
              <v-text-field
                v-model="editedItem.version"
                :label="$t('checklists.version')"
                disabled
              ></v-text-field>
            </v-col>
            <v-col sx="12" sm="10">
              <v-text-field
                v-model="editedItem.name"
                :label="$t('name')"
                data-test="new-checklist-name"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col xs="12" sm="2">
              <v-text-field
                v-model="editedItem.code"
                :label="$t('code')"
                data-test="new-checklist-code"
              ></v-text-field>
            </v-col>
            <v-col xs="12" sm="5" class="pl-3">
              <v-autocomplete
                v-model="editedItem.deviceType"
                :items="deviceTypes"
                :label="$t('devices.type')"
                :hint="$t('checklists.link-to-device-type-hint')"
                @change="setDeviceType"
                persistent-hint
                clearable
                data-test="new-asset-type-device-type"
              ></v-autocomplete>
            </v-col>
            <v-col xs="12" sm="5" class="pl-3" v-if="editedItem.id">
              <v-select
                v-model="linkedAssetTypes"
                :items="assetTypes"
                :label="$t('entityNames.asset-types')"
                :hint="$t('checklists.link-to-asset-type-hint')"
                persistent-hint
                multiple
                data-test="new-checklist-asset-type"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col xs="12" sm="4">
              <v-spacer />
              <v-switch v-model="editedItem.summary" :label="$t('checklists.summary')"></v-switch>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-btn
          @click="$router.push({ name: 'checklists' })"
          data-test="back-new-checklist"
        >{{ $t('back') }}</v-btn>
        <v-spacer />
        <v-btn
          text
          :disabled="checklistPreviewDisabled"
          @click="previewDialog = true"
          data-test="checklist-preview"
        >{{ $t('checklists.preview') }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!isUnsaved"
          @click="saveItem"
          data-test="save-new-checklist"
        >{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Checklist editor -->
    <v-card v-if="dataLoaded" class="ret-card">
      <!-- Filled alert -->
      <v-alert type="info" color="grey lighten-4 black--text" :value="editorDisabled">
        {{ $t('checklists.edit-disabled-to-prevent-structural-changes') }}
      </v-alert>
      <!-- Editor body -->
      <v-card-text class="pa-2">
        <!-- Root section -->
        <section-editor
          v-bind:eventBus="eventBus"
          v-bind:level="level"
          v-bind:index="index"
          v-bind:items="items"
        ></section-editor>
      </v-card-text>
      <!-- Editor actions -->
      <v-card-actions v-if="toolbarEnabled">
        <v-spacer />
        <v-btn v-if="isUnsaved" :disabled="!isUnsaved" @click="restore">{{ $t('restore')}}</v-btn>
        <v-btn color="primary" :disabled="!isUnsaved" @click="saveItem">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Clone modal -->
    <v-dialog v-model="clone.dialog" max-width="300">
      <v-card flat data-test="clone-dialog">
        <v-card-title class="headline">{{ $t('checklists.create-new') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="clone.name" :label="$t('name')" />
          <v-text-field v-model="clone.code" :label="$t('code')" />
          <v-text-field v-bind:value="clone.version" :label="$t('checklists.version')" readonly />
          <v-radio-group v-model="clone.version" @change="updateCloneCode">
            <v-radio :label="$t('checklists.clone')" :value="1"></v-radio>
            <v-radio
              :label="$t('checklists.new-revision')"
              :value="version+1"
              data-test="checklist-version"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="gray darken-1" @click="clone.dialog = false">{{ $t('cancel') }}</v-btn>
          <v-spacer />
          <v-btn text color="blue darken-1" @click="doClone" data-test="confirm">{{ $t('confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview modal -->
    <v-dialog v-model="previewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <h3>{{ $t('checklists.preview') }}</h3>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="previewDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <checklist-section
          v-if="previewDialog"
          :level="checklistPreview.lvl"
          :index="checklistPreview.idx"
          :items="checklistPreview.items"
          :label="checklistPreview.label"
          :description="checklistPreview.description"
          id="checklist"
        ></checklist-section>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import itemHelpers from '../helpers/item-helpers.es6'
import SectionEditor from './components/editor-section.vue'
import ChecklistSection from './components/checklist-section'

export default {
  name: 'checklist-editor',
  mixins: [itemHelpers],
  data () {
    return {
      eventBus: new Vue(),
      model: { name: 'checklists' },
      auxModels: [
        { name: 'asset-types' }
      ],
      linkedAssetTypes: null,
      previewDialog: false,
      clone: {
        dialog: false,
        name: '',
        code: '',
        version: 1
      }
    }
  },
  computed: {
    version () {
      return this.editedItem ? this.editedItem.version : 1
    },
    level () {
      return this.$store.getters['checklist/root'].lvl
    },
    index () {
      return this.$store.getters['checklist/root'].idx
    },
    items () {
      return this.$store.getters['checklist/root'].items
    },
    availableLanguages () {
      return this.$store.getters['checklist/languages']
    },
    language: {
      set (language) {
        this.$store.commit('checklist/setLanguage', { language })
      },
      get () {
        return this.$store.getters['checklist/language']
      }
    },
    deviceTypes () {
      return this.$store.state.deviceTypes
        .map((deviceType) => deviceType.name)
    },
    assetTypes () {
      return this.$store.state.items['asset-types']
        .map((assetType) => ({
          text: assetType.fullPath,
          value: assetType.id
        }))
    },
    checklistPreview () {
      return this.$store.getters['checklist/root']
    },
    checklistPreviewDisabled () {
      return this.checklistPreview.items.length === 0
    },
    editorDisabled () {
      return this.dataLoaded && !!this.item.xIsFilled
    },
    toolbarEnabled () {
      return this.$store.getters['checklist/isFilled'] === false
    },
    isClone () {
      return this.clone.version === this.editedItem.version
    }
  },
  mounted () {
    this.$store.commit('checklist/setLanguage', { language: '' })

    this.eventBus.$on('update', evt => {
      this.$store.commit('checklist/updateItem', evt)
      this.isUnsaved = true
    })

    this.eventBus.$on('insert', evt => {
      this.$store.commit('checklist/insertItem', evt)
      this.isUnsaved = true
    })

    this.eventBus.$on('remove', evt => {
      this.$store.commit('checklist/removeItem', evt)
      this.isUnsaved = true
    })

    this.eventBus.$on('uploadImage', evt => {
      const { idx, items, data } = evt
      this.$store
        .dispatch('checklist/uploadEditorImage', { formData: data })
        .then(({ id }) => {
          this.$store.commit('checklist/updateItem', {
            idx,
            items: items.concat(id)
          })
          this.isUnsaved = true
        })
        .catch(console.error)
    })

    this.eventBus.$on('removeImage', evt => {
      const { idx, items, id } = evt
      const fileIn = this.editedItem.filesInChecklists.filter((f) => f.fileId === id).shift()
      this.$store
        .dispatch('checklist/removeEditorImage', { id, fileInId: fileIn ? fileIn.id : null })
        .then(() => {
          this.$store.commit('checklist/updateItem', {
            idx,
            items: items.filter(i => i !== id)
          })
        })
        .catch(console.error)
    })
  },
  methods: {
    setDeviceType (deviceType) {
      this.$store.commit('checklist/setDeviceType', { deviceType })
    },
    openCloneDialog () {
      this.clone.name = this.editedItem.name
      this.clone.code = this.editedItem.code + (this.isClone ? '-clone' : '')
      this.clone.dialog = true
    },
    updateCloneCode () {
      const re = /-clone$/i
      if (this.isClone) {
        if (!re.test(this.clone.code)) {
          this.clone.code = this.clone.code + '-clone'
        }
      } else {
        this.clone.code = this.clone.code.replace(re, '')
      }
    },
    async doClone () {
      try {
        const { name, code, version } = this.clone
        const { id } = await this.$store.dispatch('checklist/clone', {
          id: this.editedItem.id,
          name,
          code,
          version
        })
        this.$router.push({ name: `checklists/id`, params: { id } })
      } catch (error) {
        console.error(error)
      }
    },
    async saveItem () {
      // update definition
      this.editedItem.definition = this.cleanJSON(
        this.$store.getters['checklist/root']
      )
      // summary sanitization
      this.editedItem.summary = !!this.editedItem.summary
      // save progress
      try {
        const response = await this.save({
          reRoute: false,
          snackbar: true,
          saveEditedItem: true
        })
        if (this.editedItem.id === undefined) {
          // created new
          this.$router.replace({
            name: 'checklists/id',
            params: { id: response.data.id }
          })
        } else {
          // updated
          this.$store.commit('checklist/setChecklistId', { id: this.editedItem.id })
          this.isUnsaved = false
        }
      } catch (error) {
        console.error('[checklistEditor][save]', error)
      }
    },
    restore () {
      if (this.editedItem.id === undefined) {
        this.$store.commit('checklist/restoreEditor', {
          idx: 0,
          lvl: 0,
          type: 'section',
          items: []
        })
        this.isUnsaved = false
        return
      }
      // save progress without updating definition
      this.save({ reRoute: false, snackbar: false, saveEditedItem: true })
        .then(() => {
          this.isUnsaved = false
        })
        .catch(error => {
          console.log('[checklistEditor][save]', error)
        })
    },
    cleanJSON (value) {
      return JSON.parse(JSON.stringify(value))
    }
  },
  watch: {
    item (item) {
      if (item.deviceType) {
        this.$store.commit('checklist/setDeviceType', { deviceType: item.deviceType })
      }
      if (item.id) {
        this.$store.commit('checklist/setChecklistId', { id: item.id })
        this.$store.commit('checklist/setIsFilled', { isFilled: item.xIsFilled })
      }
      if (item.definition === null || item.id === undefined) {
        // default state
        this.$store.commit('checklist/restoreEditor', {
          idx: 0,
          lvl: 0,
          type: 'section',
          items: []
        })
      }
      if (item.definition) {
        // restore state from definition
        this.$store.commit('checklist/restoreEditor', this.cleanJSON(item.definition))
      }
      if (item.checklistsInAssetTypes) {
        // set up linked asset types
        this.linkedAssetTypes = item.checklistsInAssetTypes.map(item => item.assetTypeId)
      }
    },
    linkedAssetTypes (next, prev) {
      if (prev === null) {
        return // initial value
      }

      let added, removed, promise

      for (let id of next) {
        if (prev.indexOf(id) < 0) {
          added = id
        }
      }
      for (let id of prev) {
        if (next.indexOf(id) < 0) {
          removed = id
        }
      }
      if (added) {
        promise = this.$store
          .dispatch('updateItem', {
            modelName: 'checklists-in-asset-types',
            item: {
              assetTypeId: added,
              checklistId: this.item.id
            }
          })
      }
      if (removed) {
        promise = this.$store
          .dispatch('destroyItem', {
            model: { name: 'checklists-in-asset-types' },
            id: this.item.checklistsInAssetTypes.reduce((id, item) => {
              if (item.assetTypeId === removed) {
                id = item.id
              }
              return id
            }, null)
          })
      }
      if (promise) {
        promise
          .then(() => {
            this.$store.commit('showSnackbar', { message: this.$t('saved') })
          })
          .catch(console.error)
      }
    }
  },
  components: {
    SectionEditor,
    ChecklistSection
  }
}
</script>
